
import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Separator } from "@/components/ui/separator";

interface NotificationSettings {
  email: {
    marketing: boolean;
    accountUpdates: boolean;
    newReviews: boolean;
    rankingChanges: boolean;
  };
  push: {
    newReviews: boolean;
    rankingChanges: boolean;
    recommendations: boolean;
  };
}

const NotificationsTab = () => {
  const [settings, setSettings] = useState<NotificationSettings>({
    email: {
      marketing: true,
      accountUpdates: true,
      newReviews: true,
      rankingChanges: true,
    },
    push: {
      newReviews: true,
      rankingChanges: true,
      recommendations: true,
    }
  });
  
  const [isSaving, setIsSaving] = useState(false);
  const { toast } = useToast();

  // This would normally load the user's notification preferences from the backend
  useEffect(() => {
    // Mock loading user notification settings
    console.log("Loading notification settings");
    // In a real app, you would fetch these settings from your database
  }, []);

  const handleToggle = (category: 'email' | 'push', setting: string, value: boolean) => {
    setSettings({
      ...settings,
      [category]: {
        ...settings[category],
        [setting]: value
      }
    });
  };

  const handleSave = async () => {
    setIsSaving(true);
    
    try {
      // Simulate API call to save notification preferences
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      toast({
        title: "Notification preferences saved",
        description: "Your notification settings have been updated",
      });
    } catch (error) {
      console.error("Failed to save notification preferences:", error);
      toast({
        title: "Failed to save preferences",
        description: "Something went wrong while saving your preferences",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Notification Preferences</CardTitle>
        <CardDescription>
          Manage how you receive notifications from our platform.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <h3 className="text-lg font-medium">Email Notifications</h3>
          <Separator />
          
          <div className="grid gap-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-base">Marketing Updates</Label>
                <p className="text-sm text-muted-foreground">
                  Receive emails about new features and special offers.
                </p>
              </div>
              <Switch 
                checked={settings.email.marketing}
                onCheckedChange={(checked) => handleToggle('email', 'marketing', checked)}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-base">Account Updates</Label>
                <p className="text-sm text-muted-foreground">
                  Get notified about important changes to your account.
                </p>
              </div>
              <Switch 
                checked={settings.email.accountUpdates}
                onCheckedChange={(checked) => handleToggle('email', 'accountUpdates', checked)}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-base">New Reviews</Label>
                <p className="text-sm text-muted-foreground">
                  Get emails when you receive new reviews.
                </p>
              </div>
              <Switch 
                checked={settings.email.newReviews}
                onCheckedChange={(checked) => handleToggle('email', 'newReviews', checked)}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-base">Ranking Changes</Label>
                <p className="text-sm text-muted-foreground">
                  Be notified when your rankings change significantly.
                </p>
              </div>
              <Switch 
                checked={settings.email.rankingChanges}
                onCheckedChange={(checked) => handleToggle('email', 'rankingChanges', checked)}
              />
            </div>
          </div>
        </div>
        
        <div className="space-y-4">
          <h3 className="text-lg font-medium">Push Notifications</h3>
          <Separator />
          
          <div className="grid gap-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-base">New Reviews</Label>
                <p className="text-sm text-muted-foreground">
                  Receive push notifications for new reviews.
                </p>
              </div>
              <Switch 
                checked={settings.push.newReviews}
                onCheckedChange={(checked) => handleToggle('push', 'newReviews', checked)}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-base">Ranking Changes</Label>
                <p className="text-sm text-muted-foreground">
                  Get push alerts about ranking changes.
                </p>
              </div>
              <Switch 
                checked={settings.push.rankingChanges}
                onCheckedChange={(checked) => handleToggle('push', 'rankingChanges', checked)}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-base">Recommendations</Label>
                <p className="text-sm text-muted-foreground">
                  Receive notifications about new recommendations for your business.
                </p>
              </div>
              <Switch 
                checked={settings.push.recommendations}
                onCheckedChange={(checked) => handleToggle('push', 'recommendations', checked)}
              />
            </div>
          </div>
        </div>
      </CardContent>
      <CardFooter>
        <Button onClick={handleSave} disabled={isSaving}>
          {isSaving ? (
            <>
              <Loader className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            "Save Preferences"
          )}
        </Button>
      </CardFooter>
    </Card>
  );
};

export default NotificationsTab;
